import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_EMAIL_SETTINGS,
  SAVE_EMAIL_SETTINGS,
  TEST_EMAIL_SETTINGS,
} from "../graphql/queries";

const EmailSettingsPage = () => {
  const [settings, setSettings] = useState({
    senderName: "",
    senderEmail: "",
    senderPassword: "",
    smtpHost: "",
    smtpPort: 587,
    smtpSecure: true,
  });
  const [testEmail, setTestEmail] = useState("");
  const [notification, setNotification] = useState({ open: false, message: "", severity: "info" });
  const [loading, setLoading] = useState(false);

  const { data, loading: queryLoading } = useQuery(GET_EMAIL_SETTINGS);
  const [saveSettings] = useMutation(SAVE_EMAIL_SETTINGS);
  const [testSettings] = useMutation(TEST_EMAIL_SETTINGS);

  useEffect(() => {
    if (data?.getEmailSettings) {
      const existing = data.getEmailSettings;
      setSettings({
        senderName: existing.senderName || "",
        senderEmail: existing.senderEmail || "",
        senderPassword: "", // Don't show existing password
        smtpHost: existing.smtpHost || "",
        smtpPort: existing.smtpPort || 587,
        smtpSecure: existing.smtpSecure !== false,
      });
    }
  }, [data]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await saveSettings({
        variables: settings,
      });
      setNotification({
        open: true,
        message: "Email settings saved successfully",
        severity: "success",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to save email settings",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      setNotification({
        open: true,
        message: "Please enter a test email address",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await testSettings({
        variables: { to: testEmail },
      });
      setNotification({
        open: true,
        message: "Test email sent successfully",
        severity: "success",
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || "Failed to send test email",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (queryLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading email settings...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Email Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure SMTP settings for sending emails. Passwords are encrypted and stored securely.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              SMTP Configuration
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sender Name"
                  value={settings.senderName}
                  onChange={(e) => setSettings({ ...settings, senderName: e.target.value })}
                  placeholder="e.g., Eventegration Team"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sender Email"
                  type="email"
                  value={settings.senderEmail}
                  onChange={(e) => setSettings({ ...settings, senderEmail: e.target.value })}
                  placeholder="your-email@example.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sender Password / App Password"
                  type="password"
                  value={settings.senderPassword}
                  onChange={(e) => setSettings({ ...settings, senderPassword: e.target.value })}
                  helperText="For Gmail, use App Password (not your regular password)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Host"
                  value={settings.smtpHost}
                  onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })}
                  placeholder="smtp.gmail.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Port"
                  type="number"
                  value={settings.smtpPort}
                  onChange={(e) => setSettings({ ...settings, smtpPort: parseInt(e.target.value) || 587 })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.smtpSecure}
                      onChange={(e) => setSettings({ ...settings, smtpSecure: e.target.checked })}
                    />
                  }
                  label="Use SSL/TLS (Secure Connection)"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
              >
                Save Settings
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Test Email
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TextField
              fullWidth
              label="Test Email Address"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              margin="normal"
              placeholder="test@example.com"
            />
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={handleTest}
              disabled={loading || !testEmail}
              sx={{ mt: 2 }}
            >
              Send Test Email
            </Button>
          </Paper>

          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              <InfoIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Gmail Setup Guide
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">How to create Gmail App Password</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" component="div">
                  <ol>
                    <li>Go to your Google Account settings</li>
                    <li>Navigate to Security → 2-Step Verification</li>
                    <li>Scroll down to "App passwords"</li>
                    <li>Select "Mail" and "Other (Custom name)"</li>
                    <li>Enter "Eventegration" as the name</li>
                    <li>Click "Generate"</li>
                    <li>Copy the 16-character password</li>
                    <li>Use this password in the "Sender Password" field above</li>
                  </ol>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                    Note: You must have 2-Step Verification enabled on your Google account.
                  </Typography>
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>

      {/* Notification */}
      {notification.open && (
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
          sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}
        >
          {notification.message}
        </Alert>
      )}
    </Box>
  );
};

export default EmailSettingsPage;
